// =====================================================
// PE PORTFOLIO - VERSION ALPHA v11.1
// =====================================================
// Webapp para gestión de carteras de Private Equity
// Sidebar estilo V0 exacto + PDF color #0F172A
// Branch: feature/pdf-size-optimization
// =====================================================

import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Bar, BarChart, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ComposedChart, Line, Legend, LabelList } from 'recharts';
import { Users, Eye, Download, Upload, LogOut, LayoutDashboard, TrendingUp, Key, ChevronUp, ChevronDown, Menu, X } from 'lucide-react';
import database from './database.json';
import { verifyPassword, SessionManager } from './utils/auth';
import { verifyPasswordWithOverrides } from './utils/passwordManager';
import PasswordManager from './components/PasswordManager';
import './styles.css';

// =====================================================
// COLORES CORPORATIVOS
// =====================================================
const COLORS_CORP = {
  darkBg: '#0F172A',
  button: '#A6C9EC',
  buttonText: '#1e293b',
  clientName: '#A6C9EC',
  pdfBg: '#0F172A'
};

// =====================================================
// UTILIDADES Y HELPERS
// =====================================================

const fmtFull = (v) => new Intl.NumberFormat('es-ES', { 
  style: 'currency', 
  currency: 'EUR', 
  maximumFractionDigits: 0 
}).format(v);

const fmtK = (v) => new Intl.NumberFormat('es-ES', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0
}).format(v);

// Formato número con separador de miles, SIN símbolo €
// Forzamos separador para TODOS los números (incluidos < 10.000)
const fmtNum = (v) => {
  const num = Math.round(v);
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

const fmtPercent = (v, decimals = 1) => {
  const percent = v * 100;
  const formatted = percent.toFixed(decimals);
  return percent >= 0 ? `+${formatted}%` : `${formatted}%`;
};

const fmtTVPI = (v) => `${v.toFixed(2)}x`;

const badge = (t) => {
  const c = { 
    'Buy Outs': 'bg-blue-100 text-blue-700', 
    'Secundario': 'bg-purple-100 text-purple-700', 
    'Venture': 'bg-orange-100 text-orange-700', 
    'Deuda Privada': 'bg-slate-200 text-slate-700', 
    'Search Fund': 'bg-teal-100 text-teal-700', 
    'Energias': 'bg-green-100 text-green-700',
    'Infraestructuras': 'bg-cyan-100 text-cyan-700',
    'Litigios': 'bg-rose-100 text-rose-700'
  };
  return c[t] || 'bg-slate-100 text-slate-600';
};

const COLORS = {
  'Venture': '#f97316',
  'Secundario': '#8b5cf6',
  'Buy Outs': '#3b82f6',
  'Search Fund': '#14b8a6',
  'Deuda Privada': '#64748b',
  'Energias': '#22c55e',
  'Infraestructuras': '#06b6d4',
  'Litigios': '#f43f5e'
};

// =====================================================
// COMPONENTES
// =====================================================

const KPI = ({ label, value, pos }) => (
  <div className="bg-white rounded-lg p-3 shadow-sm">
    <p className="text-xs text-slate-500">{label}</p>
    <p className={`text-base font-bold ${pos === true ? 'text-emerald-600' : pos === false ? 'text-red-500' : 'text-slate-900'}`}>
      {value}
    </p>
  </div>
);

// =====================================================
// GENERADOR DE PDF
// =====================================================

const generatePDF = async (cliente, resumenAgregado, productosCliente, trimestre, chartsRef) => {
  const { default: jsPDF } = await import('jspdf');
  const { default: html2canvas } = await import('html2canvas');
  
  const doc = new jsPDF('l', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  
  // ========== PÁGINA ÚNICA: DASHBOARD ==========
  
  doc.setFillColor(248, 250, 252);
  doc.rect(0, 0, pageWidth, pageHeight, 'F');
  
  doc.setTextColor(15, 23, 42);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text(cliente.nombre, 15, 18);
  
  doc.setFontSize(11);
  doc.setTextColor(100, 116, 139);
  doc.setFont('helvetica', 'normal');
  doc.text(`Cartera Private Equity • ${trimestre}`, 15, 26);
  
  // Clarity Partners alineado a la derecha
  doc.setTextColor(0, 0, 0);
  doc.text('Clarity Partners', pageWidth - 15, 26, { align: 'right' });
  
  doc.setDrawColor(200, 200, 200);
  doc.line(15, 32, pageWidth - 15, 32);
  
  // KPIs - Ajustado para caber en A4 landscape con márgenes seguros
  const kpiY = 38;
  const kpiWidth = 30;    // Reducido de 32mm para mejor ajuste
  const kpiGap = 2;       // Reducido de 3mm
  const kpiStartX = 12;   // Reducido de 15mm
  
  const kpis = [
    { label: 'Comprometido', value: fmtFull(resumenAgregado.comprometido) },
    { label: 'Desembolsado', value: fmtFull(resumenAgregado.desembolsado) },
    { label: 'Pendiente', value: fmtFull(resumenAgregado.pendiente) },
    { label: 'Distribuciones', value: fmtFull(resumenAgregado.distribuciones) },
    { label: 'Valoración', value: fmtFull(resumenAgregado.valoracion) },
    { label: 'Años', value: `${resumenAgregado.vida_inicio.toFixed(2)} años` },
    { label: 'TIR', value: fmtPercent(resumenAgregado.tir), color: resumenAgregado.tir >= 0 ? '#059669' : '#ef4444' },
    { label: 'TVPI', value: fmtTVPI(resumenAgregado.tvpi), color: resumenAgregado.tvpi >= 1 ? '#059669' : '#ef4444' }
  ];
  
  kpis.forEach((kpi, i) => {
    const x = kpiStartX + i * (kpiWidth + kpiGap);
    
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(x, kpiY, kpiWidth, 20, 2, 2, 'F');
    
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text(kpi.label, x + 2, kpiY + 6);
    
    doc.setFontSize(11);
    if (kpi.color) {
      const rgb = kpi.color === '#059669' ? [5, 150, 105] : [239, 68, 68];
      doc.setTextColor(...rgb);
    } else {
      doc.setTextColor(15, 23, 42);
    }
    doc.setFont('helvetica', 'bold');
    doc.text(kpi.value, x + 2, kpiY + 15);
    doc.setFont('helvetica', 'normal');
  });
  
  // ========== CONFIGURACIÓN DE OPTIMIZACIÓN PDF ==========
  // Ajustar estos valores para equilibrar calidad vs tamaño:
  // - scale: 1 = ligero (~1.5MB), 1.5 = medio (~2.5MB), 2 = pesado (~4MB)
  // - jpegQuality: 0.3 = muy comprimido, 0.5 = equilibrado, 0.7 = alta calidad
  const PDF_CONFIG = {
    scale: 3.5,           // Muy alta resolución
    jpegQuality: 0.92     // Calidad muy alta (92%) - Target: <500KB
  };

  if (chartsRef && chartsRef.current) {
    try {
      const canvas = await html2canvas(chartsRef.current, {
        scale: PDF_CONFIG.scale,
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#f8fafc',
        logging: false,
        width: chartsRef.current.offsetWidth,
        height: chartsRef.current.offsetHeight
      });

      // Comprimir a JPEG (mucho más ligero que PNG)
      const compressedImage = canvas.toDataURL('image/jpeg', PDF_CONFIG.jpegQuality);

      // Calcular dimensiones manteniendo proporción original
      const maxWidth = pageWidth - 30;
      const maxHeight = 58;

      // Proporción original del canvas
      const canvasRatio = canvas.width / canvas.height;

      // Calcular dimensiones respetando proporción
      let imgWidth = maxWidth;
      let imgHeight = imgWidth / canvasRatio;

      // Si excede altura máxima, ajustar por altura
      if (imgHeight > maxHeight) {
        imgHeight = maxHeight;
        imgWidth = imgHeight * canvasRatio;
      }

      // Centrar horizontalmente si es más estrecho
      const xOffset = 15 + (maxWidth - imgWidth) / 2;

      doc.addImage(compressedImage, 'JPEG', xOffset, 62, imgWidth, imgHeight);

      // Log para debug (quitar en producción si se desea)
      console.log(`✅ PDF generado - Config: scale=${PDF_CONFIG.scale}, quality=${PDF_CONFIG.jpegQuality}`);
    } catch (e) {
      console.log('❌ Error capturando gráficos:', e);
    }
  }
  
  // Tabla
  const tableY = 124;
  
  doc.setFontSize(12);
  doc.setTextColor(15, 23, 42);
  doc.setFont('helvetica', 'bold');
  doc.text('Detalle de Fondos', 10, tableY);

  const headers = ['Fondo', 'Tipo', 'Entidad', 'Compromiso (€)', 'Desemb. (€)', 'Pendiente (€)', 'Distrib. (€)', 'Valor (€)', 'Años', 'TIR', 'TVPI'];
  // Anchos redistribuidos: FONDO+6, TIPO+5 para "Infraestructuras", resto ajustado
  // Total = 252mm, inicio en 10mm, fin en 262mm (margen derecho 35mm)
  const colWidths = [52, 28, 20, 22, 21, 21, 19, 21, 12, 18, 18];
  const tableStartX = 10;
  let xPos = tableStartX;

  const tableWidth = colWidths.reduce((a, b) => a + b, 0);
  doc.setFillColor(241, 245, 249);
  doc.rect(tableStartX, tableY + 4, tableWidth, 8, 'F');
  
  doc.setFontSize(7);
  doc.setTextColor(100, 116, 139);
  doc.setFont('helvetica', 'bold');
  
  headers.forEach((h, i) => {
    if (i < 3) {
      // Fondo, Tipo, Entidad: alineación izquierda/centro
      doc.text(h.toUpperCase(), xPos + 1, tableY + 10);
    } else {
      // Columnas numéricas: alineación derecha
      doc.text(h.toUpperCase(), xPos + colWidths[i] - 1, tableY + 10, { align: 'right' });
    }
    xPos += colWidths[i];
  });
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  
  const sortedProducts = [...productosCliente].sort((a, b) => b.comprometido - a.comprometido);
  
  sortedProducts.slice(0, 8).forEach((f, rowIndex) => {
    const rowY = tableY + 17 + rowIndex * 7;
    xPos = tableStartX;

    if (rowIndex % 2 === 1) {
      doc.setFillColor(248, 250, 252);
      doc.rect(tableStartX, rowY - 5, tableWidth, 7, 'F');
    }
    
    doc.setTextColor(15, 23, 42);
    // Truncar a 26 caracteres máximo para caber en 52mm sin solapar con TIPO
    const nombre = f.nombre.length > 26 ? f.nombre.substring(0, 26) + '...' : f.nombre;
    doc.text(nombre, xPos + 1, rowY);
    xPos += colWidths[0];
    
    doc.setTextColor(71, 85, 105);
    doc.text(f.tipo, xPos + 1, rowY);
    xPos += colWidths[1];
    
    doc.text(f.entidad, xPos + 1, rowY);
    xPos += colWidths[2];
    
    // Columnas numéricas alineadas a la derecha, sin símbolo €
    doc.setTextColor(15, 23, 42);
    doc.text(fmtNum(f.comprometido), xPos + colWidths[3] - 1, rowY, { align: 'right' });
    xPos += colWidths[3];

    doc.text(fmtNum(f.desembolsado), xPos + colWidths[4] - 1, rowY, { align: 'right' });
    xPos += colWidths[4];

    doc.text(fmtNum(f.pendiente), xPos + colWidths[5] - 1, rowY, { align: 'right' });
    xPos += colWidths[5];

    doc.text(fmtNum(f.distribuciones), xPos + colWidths[6] - 1, rowY, { align: 'right' });
    xPos += colWidths[6];

    doc.text(fmtNum(f.valoracion), xPos + colWidths[7] - 1, rowY, { align: 'right' });
    xPos += colWidths[7];

    doc.text(f.vida_inicio.toFixed(2), xPos + colWidths[8] - 1, rowY, { align: 'right' });
    xPos += colWidths[8];

    doc.setTextColor(f.tir >= 0 ? 5 : 239, f.tir >= 0 ? 150 : 68, f.tir >= 0 ? 105 : 68);
    doc.text(fmtPercent(f.tir), xPos + colWidths[9] - 1, rowY, { align: 'right' });
    xPos += colWidths[9];

    doc.setTextColor(f.tvpi >= 1 ? 5 : 239, f.tvpi >= 1 ? 150 : 68, f.tvpi >= 1 ? 105 : 68);
    doc.text(fmtTVPI(f.tvpi), xPos + colWidths[10] - 1, rowY, { align: 'right' });
  });
  doc.save(`Informe_${cliente.nombre}_${trimestre.replace(' ', '_')}.pdf`);
};

// =====================================================
// PANTALLA DE LOGIN
// =====================================================

const Login = ({ onLogin, error }) => {
  const [clientId, setClientId] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onLogin(clientId.toUpperCase(), password);
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: COLORS_CORP.darkBg }}>
      <div className="bg-white rounded-xl shadow-lg p-8 w-96">
        <h1 className="text-xl font-bold text-slate-900 text-center mb-1">Clarity Partners</h1>
        <p className="text-sm text-slate-500 text-center mb-6">Acceso a tu cartera Private Equity</p>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">ID Cliente</label>
            <input 
              type="text" 
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              placeholder="Ej: CL01"
              className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Contraseña</label>
            <input 
              type="password" 
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </div>
          
          {error && (
            <p className="text-red-500 text-sm text-center">{error}</p>
          )}
          
          <button 
            type="submit"
            style={{ backgroundColor: COLORS_CORP.button, color: COLORS_CORP.buttonText }}
            className="w-full py-3 rounded-lg text-sm font-medium hover:opacity-90 transition"
          >
            Acceder
          </button>
        </form>
        
        <p className="text-xs text-slate-400 text-center mt-5">
          Contacta con tu gestor si olvidaste tus credenciales
        </p>
      </div>
    </div>
  );
};

// =====================================================
// VISTA CLIENTE
// =====================================================

const ClientView = ({ cliente, onLogout, isEmbedded = false }) => {
  const chartsRef = useRef(null);

  // Estado para menú móvil
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Estado para ordenamiento de tabla (por defecto: comprometido descendente)
  const [sortConfig, setSortConfig] = useState({ key: 'comprometido', direction: 'desc' });

  // Mapeo de headers a propiedades de datos (solo columnas numéricas ordenables)
  const sortableColumns = {
    'Compromiso (€)': 'comprometido',
    'Desemb. (€)': 'desembolsado',
    'Pendiente (€)': 'pendiente',
    'Distrib. (€)': 'distribuciones',
    'Valor (€)': 'valoracion',
    'Años': 'vida_inicio',
    'TIR': 'tir',
    'TVPI': 'tvpi'
  };

  const resumenAgregado = useMemo(() => {
    return database.resumen.find(r => r.id_cliente === cliente.id && r.id_producto === 'AGRE');
  }, [cliente.id]);

  const productosCliente = useMemo(() => {
    const datos = database.resumen
      .filter(r => r.id_cliente === cliente.id && r.id_producto !== 'AGRE')
      .map(r => {
        const producto = database.productos.find(p => p.id === r.id_producto);
        return {
          ...r,
          nombre: producto?.nombre || r.id_producto,
          tipo: producto?.tipo || 'N/A',
          entidad: producto?.entidad || 'N/A'
        };
      });

    // Ordenar según sortConfig
    return datos.sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      if (sortConfig.direction === 'asc') {
        return aVal - bVal;
      }
      return bVal - aVal;
    });
  }, [cliente.id, sortConfig]);

  const evolutionData = useMemo(() => {
    return database.flujos
      .filter(f => f.id_cliente === cliente.id && f.id_producto === 'AGRE')
      .map(f => ({
        year: f.año.toString(),
        desem: f.desembolso / 1000,
        dist: f.distribucion / 1000,
        saldo: f.neto / 1000
      }))
      .filter(f => f.desem !== 0 || f.dist !== 0);
  }, [cliente.id]);

  const latestYearData = useMemo(() => {
    const latest = evolutionData.find(d => d.year === '2025') || evolutionData[evolutionData.length - 1];
    return latest ? {
      desembolsos: `Desembolsos ${latest.year}: €${latest.desem.toFixed(0)}K`,
      distribuciones: `Distribuciones ${latest.year}: €${latest.dist.toFixed(0)}K`,
      saldo: `Saldo ${latest.year}: €${latest.saldo.toFixed(0)}K`
    } : null;
  }, [evolutionData]);

  const allocData = useMemo(() => {
    const byTipo = {};
    productosCliente.forEach(p => {
      const tipo = p.tipo;
      if (!byTipo[tipo]) byTipo[tipo] = 0;
      byTipo[tipo] += p.comprometido || 0;
    });

    const total = Object.values(byTipo).reduce((sum, val) => sum + val, 0);

    return Object.entries(byTipo).map(([name, value]) => {
      const percent = ((value / total) * 100).toFixed(0);
      return {
        name,
        value: Math.round(value / 1000),
        percent: Number(percent),
        percentLabel: `${percent}%`,
        color: COLORS[name] || '#94a3b8'
      };
    }).sort((a, b) => b.percent - a.percent);
  }, [productosCliente]);

  // Función para manejar click en header de columna
  const handleSort = (header) => {
    const key = sortableColumns[header];
    if (!key) return; // No es columna ordenable

    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  // Función para renderizar icono de ordenamiento
  const renderSortIcon = (header) => {
    const key = sortableColumns[header];
    if (!key) return null; // No es columna ordenable

    const isActive = sortConfig.key === key;
    const Icon = sortConfig.direction === 'asc' ? ChevronUp : ChevronDown;

    return (
      <Icon
        className={`w-3 h-3 inline-block ml-0.5 ${isActive ? 'text-slate-700' : 'text-slate-300'}`}
      />
    );
  };

  if (!resumenAgregado) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <p className="text-slate-500">No se encontraron datos para este cliente</p>
      </div>
    );
  }

  // Si trimestre empieza con "D:" (fecha de actualización), mostrar solo la fecha
  const trimestre = resumenAgregado.trimestre?.startsWith('D:')
    ? resumenAgregado.trimestre.substring(2)  // Quitar "D:" → "23/01/2026"
    : `${resumenAgregado.trimestre} ${resumenAgregado.año}`;  // Formato Q3 2025

  const handleDownloadPDF = () => {
    generatePDF(cliente, resumenAgregado, productosCliente, trimestre, chartsRef);
  };

  const mainContent = (
    <div className="flex-1 bg-slate-100 min-h-screen">
      <header className="bg-white px-5 py-3 border-b">
        <div className="flex justify-between items-center mb-1">
          <div className="flex items-center gap-3">
            {/* Botón hamburger - solo visible en móvil y no embebido */}
            {!isEmbedded && (
              <button
                onClick={() => setIsMobileMenuOpen(true)}
                className="mobile-menu-button p-1 rounded hover:bg-slate-100"
              >
                <Menu className="w-5 h-5 text-slate-700" />
              </button>
            )}
            <h1 className="text-lg font-bold text-slate-900">{cliente.nombre}</h1>
          </div>
          <button
            onClick={handleDownloadPDF}
            style={{ backgroundColor: COLORS_CORP.button, color: COLORS_CORP.buttonText }}
            className="px-3 py-1.5 rounded text-xs flex items-center gap-1 font-medium"
          >
            <Download className="w-3 h-3" />PDF
          </button>
        </div>
        <div className="flex justify-between items-center">
          <p className="text-xs text-slate-500">Cartera Private Equity • {trimestre}</p>
          <p className="text-xs text-slate-900 font-medium">Clarity Partners</p>
        </div>
      </header>

      <div className="p-5 space-y-5">
        <div className="grid grid-cols-8 gap-3">
          <KPI label="Comprometido" value={fmtFull(resumenAgregado.comprometido)} />
          <KPI label="Desembolsado" value={fmtFull(resumenAgregado.desembolsado)} />
          <KPI label="Pendiente" value={fmtFull(resumenAgregado.pendiente)} />
          <KPI label="Distribuciones" value={fmtFull(resumenAgregado.distribuciones)} />
          <KPI label="Valoración" value={fmtFull(resumenAgregado.valoracion)} />
          <KPI label="Años" value={`${resumenAgregado.vida_inicio.toFixed(2)} años`} />
          <KPI label="TIR" value={fmtPercent(resumenAgregado.tir)} pos={resumenAgregado.tir >= 0} />
          <KPI label="TVPI" value={fmtTVPI(resumenAgregado.tvpi)} pos={resumenAgregado.tvpi >= 1} />
        </div>

        <div ref={chartsRef} className="grid grid-cols-2 gap-5">
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <h3 className="font-semibold text-slate-900 text-sm mb-3">Evolución Inversión</h3>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={evolutionData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="year" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}k`} />
                  <Tooltip formatter={(v) => `€${v.toFixed(0)}K`} />
                  <Legend 
                    iconType="circle" 
                    iconSize={6} 
                    wrapperStyle={{ fontSize: '10px' }} 
                    payload={latestYearData ? [
                      { value: latestYearData.desembolsos, type: 'circle', color: '#f87171' },
                      { value: latestYearData.distribuciones, type: 'circle', color: '#22c55e' },
                      { value: latestYearData.saldo, type: 'circle', color: '#1e3a5f' }
                    ] : undefined}
                  />
                  <Bar dataKey="desem" fill="#f87171" radius={[2, 2, 0, 0]} barSize={16} name="Desembolsos" />
                  <Bar dataKey="dist" fill="#22c55e" radius={[2, 2, 0, 0]} barSize={16} name="Distribuciones" />
                  <Line type="monotone" dataKey="saldo" stroke="#1e3a5f" strokeWidth={2} dot={{ r: 2 }} name="Saldo" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 shadow-sm">
            <h3 className="font-semibold text-slate-900 text-sm mb-3">Asignación por tipo - Compromiso</h3>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={allocData}
                  layout="vertical"
                  margin={{ top: 5, right: 50, left: 80, bottom: 5 }}
                >
                  <XAxis
                    type="number"
                    domain={[0, 100]}
                    tick={{ fontSize: 10 }}
                    tickFormatter={(v) => `${v}%`}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tick={{ fontSize: 10 }}
                    axisLine={false}
                    tickLine={false}
                    width={75}
                  />
                  <Tooltip formatter={(v) => [`${v}%`, null]} />
                  <Bar dataKey="percent" radius={[0, 4, 4, 0]} barSize={16}>
                    {allocData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                    <LabelList
                      dataKey="percentLabel"
                      position="right"
                      style={{ fontSize: 10, fill: '#374151' }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b">
            <h3 className="font-semibold text-slate-900 text-sm">Detalle de Fondos</h3>
          </div>
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b">
              <tr>
                {['Fondo', 'Tipo', 'Entidad', 'Compromiso (€)', 'Desemb. (€)', 'Pendiente (€)', 'Distrib. (€)', 'Valor (€)', 'Años', 'TIR', 'TVPI'].map((h, i) => {
                  const isSortable = sortableColumns[h];
                  return (
                    <th
                      key={h}
                      onClick={() => isSortable && handleSort(h)}
                      className={`font-semibold text-slate-500 uppercase px-3 py-2 ${i < 3 ? 'text-center' : 'text-right'} ${isSortable ? 'cursor-pointer hover:text-slate-700 select-none' : ''}`}
                    >
                      {h}{renderSortIcon(h)}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {productosCliente.map((f, i) => (
                <tr key={f.id_producto} className={`border-b last:border-0 hover:bg-slate-50 ${i % 2 ? 'bg-slate-50/50' : ''}`}>
                  <td className="px-3 py-2 text-left font-medium text-slate-900">{f.nombre.length > 30 ? f.nombre.substring(0, 30) + '...' : f.nombre}</td>
                  <td className="px-3 py-2 text-center">
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${badge(f.tipo)}`}>{f.tipo}</span>
                  </td>
                  <td className="px-3 py-2 text-center text-slate-600">{f.entidad}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtNum(f.comprometido)}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtNum(f.desembolsado)}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtNum(f.pendiente)}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtNum(f.distribuciones)}</td>
                  <td className="px-3 py-2 text-right font-mono font-medium">{fmtNum(f.valoracion)}</td>
                  <td className="px-3 py-2 text-right font-mono">{f.vida_inicio.toFixed(2)}</td>
                  <td className={`px-3 py-2 text-right font-mono font-semibold ${f.tir >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                    {fmtPercent(f.tir)}
                  </td>
                  <td className={`px-3 py-2 text-right font-mono font-semibold ${f.tvpi >= 1 ? 'text-emerald-600' : 'text-red-500'}`}>
                    {fmtTVPI(f.tvpi)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  if (isEmbedded) {
    return mainContent;
  }

  // Vista cliente con sidebar - ESTILO V0 EXACTO + Responsive
  return (
    <div className="flex min-h-screen bg-slate-100">
      {/* Overlay para móvil */}
      {isMobileMenuOpen && (
        <div
          className="mobile-overlay"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - responsive */}
      <div className={`sidebar-responsive w-52 bg-slate-900 p-4 ${isMobileMenuOpen ? 'sidebar-open' : ''}`}>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-white" />
            </div>
            <span className="text-white font-semibold text-sm">PE Portfolio</span>
          </div>
          {/* Botón cerrar - solo visible en móvil */}
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="mobile-close-button p-1 rounded hover:bg-slate-800"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>
        <nav className="space-y-1">
          <button className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm bg-slate-800 text-white">
            <LayoutDashboard className="w-4 h-4" />Vista Cliente
          </button>
        </nav>
        <div className="mt-auto pt-4 border-t border-slate-800 mt-8">
          <button
            onClick={onLogout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-400 hover:bg-slate-800"
          >
            <LogOut className="w-4 h-4" />Cerrar Sesión
          </button>
        </div>
      </div>
      {mainContent}
    </div>
  );
};

// =====================================================
// PANEL ADMIN
// =====================================================

const AdminPanel = ({ onLogout }) => {
  const [selectedClient, setSelectedClient] = useState(null);
  const [activeView, setActiveView] = useState('clients'); // 'clients' o 'passwords'
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const clientesResumen = useMemo(() => {
    return database.clientes.map(c => {
      const agregado = database.resumen.find(r => r.id_cliente === c.id && r.id_producto === 'AGRE');
      return {
        ...c,
        comprometido: agregado?.comprometido || 0,
        desembolsado: agregado?.desembolsado || 0,
        pendiente: agregado?.pendiente || 0,
        distribuciones: agregado?.distribuciones || 0,
        valoracion: agregado?.valoracion || 0,
        trimestre: agregado
          ? (agregado.trimestre?.startsWith('D:') ? agregado.trimestre.substring(2) : `${agregado.trimestre} ${agregado.año}`)
          : 'N/A',
        tir: agregado?.tir || 0,
        tvpi: agregado?.tvpi || 0
      };
    }).filter(c => c.valoracion > 0);
  }, []);


  // Si hay cliente seleccionado, mostrar su vista
  if (selectedClient) {
    return (
      <div className="flex min-h-screen bg-slate-100">
        {/* Overlay para móvil */}
        {isMobileMenuOpen && (
          <div
            className="mobile-overlay"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}

        {/* Sidebar - responsive */}
        <div className={`sidebar-responsive w-52 bg-slate-900 p-4 ${isMobileMenuOpen ? 'sidebar-open' : ''}`}>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-white" />
              </div>
              <span className="text-white font-semibold text-sm">PE Portfolio</span>
            </div>
            {/* Botón cerrar - solo visible en móvil */}
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="mobile-close-button p-1 rounded hover:bg-slate-800"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
          <nav className="space-y-1">
            <button
              onClick={() => { setSelectedClient(null); setIsMobileMenuOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-400 hover:bg-slate-800"
            >
              <Users className="w-4 h-4" />Panel Admin
            </button>
            <button
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm bg-slate-800 text-white border-l-2 border-teal-500"
            >
              <LayoutDashboard className="w-4 h-4" />Vista Cliente
            </button>
          </nav>
          <div className="mt-auto pt-4 border-t border-slate-800 mt-8">
            <button
              onClick={onLogout}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-400 hover:bg-slate-800"
            >
              <LogOut className="w-4 h-4" />Cerrar Sesión
            </button>
          </div>
        </div>

        {/* Contenido - Vista del cliente seleccionado con hamburger */}
        <div className="flex-1 bg-slate-100 min-h-screen">
          {/* Header móvil con hamburger */}
          <header className="admin-client-header bg-white px-5 py-3 border-b">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsMobileMenuOpen(true)}
                className="mobile-menu-button p-1 rounded hover:bg-slate-100"
              >
                <Menu className="w-5 h-5 text-slate-700" />
              </button>
              <h1 className="text-lg font-bold text-slate-900">{selectedClient.nombre}</h1>
            </div>
          </header>
          <ClientView cliente={selectedClient} onLogout={() => setSelectedClient(null)} isEmbedded={true} />
        </div>
      </div>
    );
  }

  // Vista principal Admin
  return (
    <div className="flex min-h-screen bg-slate-100">
      {/* Overlay para móvil */}
      {isMobileMenuOpen && (
        <div
          className="mobile-overlay"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - responsive */}
      <div className={`sidebar-responsive w-52 bg-slate-900 p-4 ${isMobileMenuOpen ? 'sidebar-open' : ''}`}>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-white" />
            </div>
            <span className="text-white font-semibold text-sm">PE Portfolio</span>
          </div>
          {/* Botón cerrar - solo visible en móvil */}
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="mobile-close-button p-1 rounded hover:bg-slate-800"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>
        <nav className="space-y-1">
          <button
            onClick={() => { setActiveView('clients'); setIsMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
              activeView === 'clients' ? 'bg-slate-800 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-white'
            }`}
          >
            <Users className="w-4 h-4" />Clientes
          </button>
          <button
            onClick={() => { setActiveView('passwords'); setIsMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
              activeView === 'passwords' ? 'bg-slate-800 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-white'
            }`}
          >
            <Key className="w-4 h-4" />Contraseñas
          </button>
        </nav>
        <div className="mt-auto pt-4 border-t border-slate-800 mt-8">
          <button
            onClick={onLogout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-400 hover:bg-slate-800"
          >
            <LogOut className="w-4 h-4" />Cerrar Sesión
          </button>
        </div>
      </div>

      {/* Contenido Principal */}
      <div className="flex-1">
        {/* Header móvil con hamburger */}
        <header className="admin-mobile-header bg-white px-5 py-3 border-b">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="mobile-menu-button p-1 rounded hover:bg-slate-100"
            >
              <Menu className="w-5 h-5 text-slate-700" />
            </button>
            <h1 className="text-lg font-bold text-slate-900">Panel Admin</h1>
          </div>
        </header>
        <div className="p-6">
        {activeView === 'clients' ? (
          <>
            <h1 className="text-xl font-bold text-slate-900 mb-1">Panel de Administración</h1>
            <p className="text-slate-500 text-sm mb-6">Gestiona los reportes de tus clientes</p>
            
            {/* Upload Excel */}
            <div className="bg-white rounded-xl p-6 border-2 border-dashed border-slate-200 hover:border-blue-300 cursor-pointer mb-6 text-center">
              <Upload className="w-10 h-10 mx-auto mb-2" style={{ color: COLORS_CORP.button }} />
              <h3 className="font-semibold text-slate-900">Subir Excel Trimestral</h3>
              <p className="text-sm text-slate-500 mb-3">Un único archivo con todos los clientes</p>
              <button 
                style={{ backgroundColor: COLORS_CORP.button, color: COLORS_CORP.buttonText }}
                className="px-5 py-2 rounded-lg text-sm font-medium"
              >
                Seleccionar Archivo
              </button>
            </div>

            {/* Tabla de Clientes */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b">
            <h3 className="font-semibold text-slate-900 text-sm">Clientes</h3>
            <p className="text-xs text-slate-500">{clientesResumen.length} clientes activos</p>
          </div>
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b">
              <tr>
                {['Cliente', 'Compromiso', 'Desembolsado', 'Pendiente', 'Distribuciones', 'Valoración', 'TIR', 'Acciones'].map(h => (
                  <th key={h} className="text-center font-semibold text-slate-500 uppercase px-3 py-2">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {clientesResumen.map((c, i) => (
                <tr key={c.id} className={`border-b last:border-0 hover:bg-slate-50 ${i % 2 ? 'bg-slate-50/50' : ''}`}>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center text-white text-xs font-medium">
                        {c.nombre.split(' ').map(n => n[0]).slice(0,2).join('')}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{c.nombre}</p>
                        <p className="text-slate-400">{c.id} • {c.trimestre}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-center font-mono">{fmtK(c.comprometido)}</td>
                  <td className="px-3 py-2 text-center font-mono">{fmtK(c.desembolsado)}</td>
                  <td className="px-3 py-2 text-center font-mono">{fmtK(c.pendiente)}</td>
                  <td className="px-3 py-2 text-center font-mono">{fmtK(c.distribuciones)}</td>
                  <td className="px-3 py-2 text-center font-mono font-medium">{fmtK(c.valoracion)}</td>
                  <td className={`px-3 py-2 text-center font-mono font-semibold ${c.tir >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                    {fmtPercent(c.tir)}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button 
                        onClick={() => setSelectedClient(c)} 
                        className="p-1.5 hover:bg-slate-100 rounded"
                        title="Ver cartera"
                      >
                        <Eye className="w-3.5 h-3.5 text-slate-400" />
                      </button>
                      <button className="p-1.5 hover:bg-slate-100 rounded" title="Descargar PDF">
                        <Download className="w-3.5 h-3.5 text-slate-400" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
          </>
        ) : (
          <>
            <h1 className="text-xl font-bold text-slate-900 mb-1">Gestión de Contraseñas</h1>
            <p className="text-slate-500 text-sm mb-6">Cambia las contraseñas de los clientes</p>
            
            <PasswordManager />
          </>
        )}
        </div>
      </div>
    </div>
  );
};

// =====================================================
// APP PRINCIPAL
// =====================================================

export default function App() {
  const [user, setUser] = useState(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);


  // Verificar sesión existente al cargar
  useEffect(() => {
    const existingSession = SessionManager.getSession();
    if (existingSession) {
      setUser(existingSession);
      SessionManager.extendSession(); // Extender sesión si está activa
    }
    setIsLoading(false);
  }, []);

  const handleLogin = async (clientId, password) => {
    setError('');
    
    try {
      // Verificar admin
      if (clientId === process.env.REACT_APP_ADMIN_USER) {
        if (await verifyPasswordWithOverrides('ADMIN', password, verifyPassword)) {
          const userData = { type: 'admin' };
          setUser(userData);
          SessionManager.setSession(userData);
          return;
        } else {
          setError('Contraseña incorrecta');
          return;
        }
      }

      // Verificar cliente
      const cliente = database.clientes.find(c => c.id === clientId);
      if (cliente) {
        if (await verifyPasswordWithOverrides(clientId, password, verifyPassword)) {
          const userData = { type: 'client', data: cliente };
          setUser(userData);
          SessionManager.setSession(userData);
        } else {
          setError('Contraseña incorrecta');
        }
      } else {
        setError('ID de cliente no encontrado');
      }
    } catch (err) {
      setError('Error de autenticación');
      console.error('Login error:', err);
    }
  };

  const handleLogout = () => {
    SessionManager.clearSession();
    setUser(null);
    setError('');
  };

  // Mostrar loading mientras se verifica sesión
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: COLORS_CORP.darkBg }}>
        <div className="text-white">Cargando...</div>
      </div>
    );
  }

  if (!user) {
    return <Login onLogin={handleLogin} error={error} />;
  }

  if (user.type === 'admin') {
    return <AdminPanel onLogout={handleLogout} />;
  }

  return <ClientView cliente={user.data} onLogout={handleLogout} />;
}
